import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition, SimpleSnackBar, MatSnackBarRef, MatSnackBarConfig } from '@angular/material/snack-bar';
import { HandlerMessageOptions, MessageType, HandlerErrorOptions } from './handler-message.types';

/**
 * Centralized service for message and error handling in the application.
 * Encapsulates the message display logic using MatSnackBar
 * with a consistent and standardized format.
 *
 * Supports different message types: error, warning, success, info
 *
 * @export
 * @class HandlerMessageService
 * @Injectable providedIn: 'root'
 */
@Injectable({
  providedIn: 'root',
})
export class HandlerMessageService {
  /**
   * Default duration for the snackbar in milliseconds
   * @private
   * @constant
   */
  private readonly DEFAULT_DURATION = 5000;

  /**
   * Default action button text
   * @private
   * @constant
   */
  private readonly DEFAULT_ACTION_TEXT = 'Cerrar';

  /**
   * Default horizontal position for the snackbar
   * @private
   * @constant
   */
  private readonly DEFAULT_HORIZONTAL_POSITION: MatSnackBarHorizontalPosition = 'center';

  /**
   * Default vertical position for the snackbar
   * @private
   * @constant
   */
  private readonly DEFAULT_VERTICAL_POSITION: MatSnackBarVerticalPosition = 'bottom';

  /**
   * CSS classes for different message types
   * @private
   * @constant
   */
  private readonly MESSAGE_STYLES = {
    [MessageType.ERROR]: 'snackbar-error',
    [MessageType.WARNING]: 'snackbar-warning',
    [MessageType.SUCCESS]: 'snackbar-success',
    [MessageType.INFO]: 'snackbar-info',
  };

  /**
   * Service constructor
   * @param {MatSnackBar} snackBar - Angular Material service to display snackbars
   */
  constructor(private readonly snackBar: MatSnackBar) {}

  /**
   * Handles and displays an error using MatSnackBar.
   * @param {HandlerMessageOptions} options - Error configuration options
   * @returns {MatSnackBarRef<SimpleSnackBar>}
   *
   * @example
   * ```typescript
   * this.handlerMessage.error({
   *   message: error?.error?.message,
   *   alternativeMessage: 'Error loading data',
   *   statusCode: error?.status,
   *   duration: 5000
   * });
   * ```
   */
  public error(options: HandlerMessageOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.showMessage({ ...options, type: MessageType.ERROR });
  }

  /**
   * Displays a warning message using MatSnackBar.
   * @param {HandlerMessageOptions} options - Warning configuration options
   * @returns {MatSnackBarRef<SimpleSnackBar>}
   *
   * @example
   * ```typescript
   * this.handlerMessage.warning({
   *   message: 'Los cambios no se han guardado',
   *   duration: 5000
   * });
   * ```
   */
  public warning(options: HandlerMessageOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.showMessage({ ...options, type: MessageType.WARNING });
  }

  /**
   * Displays a success message using MatSnackBar.
   * @param {HandlerMessageOptions} options - Success configuration options
   * @returns {MatSnackBarRef<SimpleSnackBar>}
   *
   * @example
   * ```typescript
   * this.handlerMessage.success({
   *   message: 'Operación completada exitosamente',
   *   duration: 3000
   * });
   * ```
   */
  public success(options: HandlerMessageOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.showMessage({ ...options, type: MessageType.SUCCESS });
  }

  /**
   * Displays an info message using MatSnackBar.
   * @param {HandlerMessageOptions} options - Info configuration options
   * @returns {MatSnackBarRef<SimpleSnackBar>}
   *
   * @example
   * ```typescript
   * this.handlerMessage.info({
   *   message: 'Nueva actualización disponible',
   *   duration: 5000
   * });
   * ```
   */
  public info(options: HandlerMessageOptions): MatSnackBarRef<SimpleSnackBar> {
    return this.showMessage({ ...options, type: MessageType.INFO });
  }

  /**
   * Generic method to display any type of message.
   * This is the core method that all public methods use internally.
   * @private
   * @param {HandlerMessageOptions} options - Message configuration options
   * @returns {MatSnackBarRef<SimpleSnackBar>}
   */
  private showMessage(options: HandlerMessageOptions): MatSnackBarRef<SimpleSnackBar> {
    const finalMessage = this.buildMessage(options);
    
    // Use provided duration only if it's greater than or equal to default, otherwise use default
    const duration = (options.duration && options.duration >= this.DEFAULT_DURATION) 
      ? options.duration 
      : this.DEFAULT_DURATION;

    const snackBarConfig: MatSnackBarConfig = {
      panelClass: this.getPanelClass(options.type || MessageType.INFO),
      duration: duration,
      horizontalPosition: options.horizontalPosition || this.DEFAULT_HORIZONTAL_POSITION,
      verticalPosition: options.verticalPosition || this.DEFAULT_VERTICAL_POSITION,
    };

    return this.snackBar.open(finalMessage, this.DEFAULT_ACTION_TEXT, snackBarConfig);
  }

  /**
   * Builds the message according to the provided options.
   * Applies the priority logic: statusCode + message > message > alternativeMessage
   *
   * @private
   * @param {HandlerMessageOptions} options - Message configuration options
   * @returns {string} Formatted message
   */
  private buildMessage(options: HandlerMessageOptions): string {
    const { message, alternativeMessage, statusCode, type } = options;

    // Determine base message with type-specific defaults
    const defaultMessage = this.getDefaultMessage(type || MessageType.INFO);
    const baseMessage = message || alternativeMessage || defaultMessage;

    // If statusCode exists (mainly for errors), add it to the message
    if (statusCode !== undefined && statusCode !== null) {
      return `[${statusCode}] ${baseMessage}`;
    }

    return baseMessage;
  }

  /**
   * Gets the CSS panel class for the message type
   * @private
   * @param {MessageType} type - Message type
   * @returns {string} CSS class name
   */
  private getPanelClass(type: MessageType): string {
    return this.MESSAGE_STYLES[type] || this.MESSAGE_STYLES[MessageType.INFO];
  }

  /**
   * Gets the default message for each message type
   * @private
   * @param {MessageType} type - Message type
   * @returns {string} Default message
   */
  private getDefaultMessage(type: MessageType): string {
    const messages = {
      [MessageType.ERROR]: 'Ha ocurrido un error',
      [MessageType.WARNING]: 'Advertencia',
      [MessageType.SUCCESS]: 'Operación exitosa',
      [MessageType.INFO]: 'Información',
    };

    return messages[type] || messages[MessageType.INFO];
  }
}

/**
 * Alias for backwards compatibility
 * @deprecated Use HandlerMessageService instead
 */
export const HandlerErrorService = HandlerMessageService;

